import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomInput, CustomTextarea } from "@components/Forms";
import { Breadcrumb, FileUploader } from "@components/UI";
import FullScreenDotLoader from "@components/UI/FullScreenDotLoader";
import queryClient from "@configs/react-query/query-client";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { useFileGroupAPI } from "@hooks/useFileGroupAPI";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { uppercase } from "@utils/common";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";
import { CHARACTER_LENGTH } from "@constants/index";

export default function ArchiveAdd() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { uploadFilesWithCustomCallback } = useFileGroupAPI(0);

  const [formData, setFormData] = useState({
    title: "",
    content: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // File upload state
  const [selectedFiles, setSelectedFiles] = useState([]);

  const addArchive = useMutationData(
    queryKeys.addArchive,
    { method: "POST" },
    {
      onSuccess: async (response) => {
        await updateDataInIndexedDb(STORES.ARCHIVES, response, response.archiveSeq);
        showSuccessToast(t("bulletin.archive-add-success"));
        queryClient.invalidateQueries(queryKeys.getArchiveList);

        // Handle file upload if files are selected
        if (selectedFiles.length > 0) {
          const formDataForUpload = new FormData();
          formDataForUpload.append("targetSeq", response.archiveSeq);
          formDataForUpload.append("targetTypeCd", "ARCHIVE");
          formDataForUpload.append("fileGroupNo", response.fileGroupNo ? response.fileGroupNo : 0);

          const uploadFilesData = selectedFiles.reduce((acc, file, index) => {
            if (file.file instanceof File) {
              acc[`file_${index}`] = file.file;
            }
            return acc;
          }, {});

          Object.entries(uploadFilesData).forEach(([key, file]) => {
            formDataForUpload.append(key, file, file.name);
          });

          uploadFilesWithCustomCallback.mutate(formDataForUpload, {
            onSuccess: () => {
              showSuccessToast(t("common.files-upload-success"));
              navigate(`/archive/${response.archiveSeq}`);
            },
            onError: (error) => {
              console.error("Error during upload data:", error);
              navigate(`/archive/${response.archiveSeq}`);
              showErrorToast(t("common.files-upload-error"));
            },
          });
        } else {
          navigate(`/archive/${response.archiveSeq}`);
        }
      },
      onError: (error) => {
        console.error("Error during add archive:", error);
        showErrorToast(t("bulletin.archive-add-error"));
      },
    },
  );

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = t("bulletin.title-required");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await addArchive.mutateAsync(formData);
      queryClient.invalidateQueries(queryKeys.getArchiveList);
    } catch (error) {
      console.error("Error submitting archive:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/archive");
  };

  const breadcrumbItems = [
    { label: t("bulletin.archive-list-title"), href: "/archive" },
    { label: t("bulletin.add-archive-modal-title") },
  ];

  const isFormValid = formData.title.trim();

  return (
    <div data-testid="archive-add-page">
      {/* Header */}
      <div className={`${colors.bgSecondary} ${colors.borderPrimary}`} data-testid="archive-add-header">
        <div className="flex items-center space-x-4">
          <Breadcrumb data-testid="archive-add-breadcrumb" items={breadcrumbItems} />
        </div>

        <div className="flex items-center justify-between mt-4">
          <h1
            className={`${colors.textPrimary} text-2xl font-bold`}
            data-testid="archive-add-page-title"
          >
            {uppercase(t("bulletin.add-archive-modal-title"))}
          </h1>

          <div className="flex items-center space-x-3" data-testid="archive-add-header-actions">
            <OutlineButton
              data-testid="archive-add-cancel-button"
              onClick={handleCancel}
              className={`rounded-lg border px-4 py-1 font-semibold focus:outline-none ${colors.textSecondary} ${colors.borderHoverPrimary} ${colors.textHoverActive}`}
            >
              {t("common.cancel-button")}
            </OutlineButton>

            <PrimaryButton data-testid="archive-add-save-button" onClick={handleSubmit} disabled={!isFormValid || addArchive.isPending}>
              {addArchive.isPending ? (
                <div className="flex items-center">
                  <div className={`mr-2 h-4 w-4 animate-spin rounded-full border-b-2 ${colors.borderOnPrimary}`}></div>
                  {t("common.saving")}
                </div>
              ) : (
                t("common.save-button")
              )}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`mt-5 p-6 ${colors.bgPrimary} rounded-2xl`} data-testid="archive-add-form">
        <div className={`mx-auto max-w-[56rem]`}>
          <div>
            {/* Archive Information Section */}
            <div className="mb-6" data-testid="archive-add-information-section">
              <h2
                className={`${colors.textPrimary} text-xl font-semibold pt-2 pb-6`}
                data-testid="archive-add-information-title"
              >
                {t("bulletin.archive-information-title")}
              </h2>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Title */}
                <div className="md:col-span-2">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("bulletin.title")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomInput
                    data-testid="archive-add-title-input"
                    type="text"
                    placeholder={t("bulletin.title-placeholder")}
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    title={formData.title}
                    className={`h-10 w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none truncate ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} transition-colors duration-300`}
                    error={errors.title}
                    maxLength={CHARACTER_LENGTH.COMMENT}
                  />
                </div>

                {/* Content */}
                <div className="md:col-span-2">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("bulletin.content")}
                  </label>
                  <CustomTextarea
                    data-testid="archive-add-content-textarea"
                    rows={4}
                    placeholder={t("bulletin.enter-content")}
                    value={formData.content}
                    onChange={(e) => handleInputChange("content", e.target.value)}
                    title={formData.content}
                    className={`w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} resize-none`}
                    error={errors.content}
                    size="sm"
                    maxLength={CHARACTER_LENGTH.CONTENT}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="archive-add-content-textarea-char-count">{(formData.content || "").length}/{CHARACTER_LENGTH.CONTENT}</p>
                </div>
              </div>
            </div>

            {/* Documents Section */}
            <div className="mb-6" data-testid="archive-add-attachments-section">
              <h2
                className={`${colors.textPrimary} mb-2 text-lg font-semibold`}
                data-testid="archive-add-attachments-title"
              >
                {uppercase(t("bulletin.attachment"))}
              </h2>
              <FileUploader
                data-testid="archive-add-file-uploader"
                onFilesChange={(files) => setSelectedFiles(files)}
                selectedFiles={selectedFiles}
                multiple={true}
              />
            </div>
          </div>
        </div>
      </div>
      <FullScreenDotLoader
        loading={uploadFilesWithCustomCallback.isPending}
        text={
          uploadFilesWithCustomCallback.isPending
            ? t("bulletin.uploading-archive") || "Uploading archive file..."
            : t("bulletin.upload-archive-file") || "Uploading archive file..."
        }
        size={80}
        speedMultiplier={1.2}
      />
    </div>
  );
}
